import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Paperclip, ChevronLeft, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { VALID_STATUSES } from '../../firebase/leads.service';
import { auth } from '../../firebase/firebase.config';
import { sendBroadcast, validateBroadcast } from '../../firebase/broadcast.service';
import { filesToValidatedFiles } from '../../utils/attachmentHelpers';
import { truncateMessagePreview, parseBroadcastTemplate } from '../../utils/inboxHelpers';
import useAppStore from '../../store/appStore';

// Broadcast variables available for message templates
const BROADCAST_VARIABLES = [
    { key: 'MyName', label: '{{MyName}}' },
    { key: 'FirstName', label: '{{FirstName}}' },
    { key: 'LastName', label: '{{LastName}}' },
    { key: 'FullName', label: '{{FullName}}' },
    { key: 'InterestedProperty', label: '{{Interested Property}}' },
    { key: 'MyPhone', label: '{{MyPhone}}' },
];

const MobileBroadcastModal = ({ onClose, onToastShow }) => {
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedTags, setSelectedTags] = useState([]);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [channel, setChannel] = useState('SMS');
    // attachments kept as File objects until Send (upload happens inside sendBroadcast)
    const [attachments, setAttachments] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendProgress, setSendProgress] = useState({ processed: 0, total: 0 });
    const [sendResult, setSendResult] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);
    const [selectedLeadIds, setSelectedLeadIds] = useState(null); // null = all selected
    const textareaRef = useRef(null);
    const subjectInputRef = useRef(null);
    const fileInputRef = useRef(null);

    // Read leads and user profile from centralized Zustand store
    const storeLeads = useAppStore((s) => s.leads);
    const userProfile = useAppStore((s) => s.userProfile);
    const leads = storeLeads || [];
    const loadingLeads = storeLeads === null;

    const handleInsertVariable = (variable, targetRef, value, setValue) => {
        const ref = targetRef || textareaRef;
        const currentValue = value !== undefined ? value : message;
        const setCurrentValue = setValue || setMessage;
        if (ref.current) {
            const start = ref.current.selectionStart;
            const end = ref.current.selectionEnd;
            const newValue = currentValue.slice(0, start) + variable.label + currentValue.slice(end);
            setCurrentValue(newValue);
            setTimeout(() => {
                ref.current.selectionStart = start + variable.label.length;
                ref.current.selectionEnd = start + variable.label.length;
                ref.current.focus();
            }, 0);
        }
    };

    const handleAttachFile = () => fileInputRef.current?.click();
    const handleFileChange = (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        try {
            const validFiles = filesToValidatedFiles(files);
            setAttachments(prev => [...prev, ...validFiles]);
        } catch (err) {
            console.error('Attachment error:', err);
            setValidationErrors([err.message || 'Attachment error']);
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };
    const handleRemoveFile = (idx) => {
        setAttachments(prev => prev.filter((_, i) => i !== idx));
    };

    // Derive available tags from all leads
    const availableTags = useMemo(() => {
        const tagSet = new Set();
        leads.forEach(lead => {
            (lead.tags || []).forEach(tag => tagSet.add(tag));
        });
        return [...tagSet].sort();
    }, [leads]);

    // Get leads filtered by status & tags (the pool from which checkbox selection applies)
    const filteredPool = useMemo(() => {
        let filtered = leads;
        if (selectedStatus !== 'All') {
            filtered = filtered.filter(lead => lead.status === selectedStatus);
        }
        if (selectedTags.length > 0) {
            filtered = filtered.filter(lead => {
                const leadTags = (lead.tags || []).map(t => t.toLowerCase());
                return selectedTags.every(tag => leadTags.includes(tag.toLowerCase()));
            });
        }
        return filtered;
    }, [leads, selectedStatus, selectedTags]);

    // Reset checkbox selection when filters change
    useEffect(() => {
        setSelectedLeadIds(null);
    }, [selectedStatus, selectedTags]);

    // Final selected leads
    const getFilteredLeads = () => {
        if (selectedLeadIds === null) return filteredPool;
        return filteredPool.filter(lead => selectedLeadIds.has(lead.id));
    };

    const getFilteredLeadCount = () => getFilteredLeads().length;

    // Checkbox helpers
    const isLeadSelected = (leadId) => selectedLeadIds === null || selectedLeadIds.has(leadId);
    const isAllSelected = selectedLeadIds === null || (filteredPool.length > 0 && filteredPool.every(l => selectedLeadIds.has(l.id)));

    const toggleLeadSelection = (leadId) => {
        setSelectedLeadIds(prev => {
            if (prev === null) {
                const allIds = new Set(filteredPool.map(l => l.id));
                allIds.delete(leadId);
                return allIds;
            }
            const next = new Set(prev);
            if (next.has(leadId)) next.delete(leadId);
            else next.add(leadId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedLeadIds(new Set());
        } else {
            setSelectedLeadIds(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex flex-col bg-white">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-notion-border bg-white">
                <div className="flex items-center gap-2">
                    <button onClick={onClose} className="text-stone-600 hover:text-stone-800">
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h2 className="font-semibold text-sm text-notion-text">New Broadcast</h2>
                        <p className="text-xs text-notion-text-light">Target specific segments.</p>
                    </div>
                </div>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition">
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Filters Section */}
                <div className="bg-notion-sidebar rounded-lg p-3 border border-notion-border space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-notion-text uppercase tracking-wide">Filters</span>
                        <span className="px-2 py-0.5 bg-stone-800 text-white rounded text-[10px] font-bold">{getFilteredLeadCount()} Selected</span>
                    </div>

                    <div className="space-y-3">
                        <div>
                            <label className="block text-[10px] font-medium text-notion-text-light mb-2">Status</label>
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                className="w-full text-xs bg-white border border-notion-border rounded px-2 py-2 focus:outline-none"
                            >
                                <option value="All">All Statuses</option>
                                {VALID_STATUSES.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>

                        {/* Tags Filter */}
                        {availableTags.length > 0 && (
                            <div>
                                <label className="block text-[10px] font-medium text-notion-text-light mb-2">Tags</label>
                                {selectedTags.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {selectedTags.map(tag => (
                                            <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-100 text-stone-600 text-[10px] rounded border border-stone-200">
                                                {tag}
                                                <button onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))} className="text-stone-400 hover:text-stone-700">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <select
                                    value=""
                                    onChange={(e) => {
                                        if (e.target.value && !selectedTags.includes(e.target.value)) {
                                            setSelectedTags(prev => [...prev, e.target.value]);
                                        }
                                    }}
                                    className="w-full text-xs bg-white border border-notion-border rounded px-2 py-2 focus:outline-none"
                                >
                                    <option value="">Filter by tag...</option>
                                    {availableTags
                                        .filter(t => !selectedTags.includes(t))
                                        .map(tag => (
                                            <option key={tag} value={tag}>{tag}</option>
                                        ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Checkbox Lead Selection */}
                    {filteredPool.length > 0 && (
                        <div className="mt-3 bg-white border border-notion-border rounded-lg overflow-hidden">
                            <div className="px-4 py-2 border-b border-notion-border bg-stone-50 flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    onChange={toggleSelectAll}
                                    className="accent-stone-800 w-3.5 h-3.5"
                                />
                                <span className="text-xs font-medium text-notion-text">Select All ({filteredPool.length})</span>
                            </div>
                            <div className="max-h-40 overflow-y-auto">
                                {filteredPool.map((lead) => (
                                    <div key={lead.id} className="px-4 py-2 flex items-center gap-2 border-b border-notion-border last:border-b-0 hover:bg-stone-50 transition">
                                        <input
                                            type="checkbox"
                                            checked={isLeadSelected(lead.id)}
                                            onChange={() => toggleLeadSelection(lead.id)}
                                            className="accent-stone-800 w-3.5 h-3.5 flex-shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <span className="text-xs text-notion-text truncate block">
                                                {lead.fullName || lead.name || 'Unnamed Lead'}
                                            </span>
                                            <span className="text-[10px] text-stone-400 truncate block">
                                                {channel === 'SMS' ? lead.phone : lead.email}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Channel Selection */}
                <div className="space-y-2">
                    {channel === 'EMAIL' && (
                        <div className="mb-2">
                            <label className="text-xs text-notion-text-light">Subject</label>
                            <div className="relative border border-notion-border rounded-lg shadow-sm mt-1">
                                <input
                                    ref={subjectInputRef}
                                    type="text"
                                    placeholder="Enter email subject (supports {{variables}})"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    disabled={sending}
                                    className="w-full px-3 py-2 text-sm rounded-t-lg focus:outline-none disabled:bg-stone-50"
                                />
                                <div className="bg-notion-sidebar border-t border-notion-border rounded-b-lg px-2 py-1 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
                                    <span className="text-[10px] text-notion-text-light font-medium mr-0.5 flex-shrink-0">Insert:</span>
                                    {BROADCAST_VARIABLES.map((variable) => (
                                        <button
                                            key={variable.key}
                                            onClick={() => handleInsertVariable(variable, subjectInputRef, subject, setSubject)}
                                            className="px-1.5 py-0.5 bg-white border border-notion-border rounded text-[10px] text-stone-600 hover:bg-stone-50 transition flex-shrink-0"
                                        >
                                            {variable.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="b-chan"
                                checked={channel === 'SMS'}
                                onChange={() => setChannel('SMS')}
                                className="accent-stone-800"
                            />
                            <span className={`text-xs font-medium ${channel === 'SMS' ? 'text-notion-text' : 'text-notion-text-light'}`}>SMS</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="b-chan"
                                checked={channel === 'EMAIL'}
                                onChange={() => setChannel('EMAIL')}
                                className="accent-stone-800"
                            />
                            <span className={`text-xs font-medium ${channel === 'EMAIL' ? 'text-notion-text' : 'text-notion-text-light'}`}>Email</span>
                        </label>
                    </div>
                </div>

                {/* Message Section */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-notion-text">Message</span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleAttachFile}
                                className="text-xs flex items-center gap-1 text-stone-500 hover:text-stone-800 transition-colors"
                            >
                                <Paperclip className="w-3 h-3" /> Attach
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={handleFileChange}
                                accept=".pdf,image/*,.doc,.docx,.txt"
                                className="hidden"
                            />
                        </div>
                    </div>

                    {attachments.length > 0 && (
                        <div className="mb-2 space-y-1">
                            {attachments.map((a, idx) => (
                                <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-stone-50 border border-notion-border rounded text-xs">
                                    <Paperclip className="w-3 h-3 text-stone-500" />
                                    <span className="flex-1 text-stone-700 truncate">{a.filename}</span>
                                    <X className="w-4 h-4 cursor-pointer text-stone-400 hover:text-stone-800" onClick={() => handleRemoveFile(idx)} />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="relative border border-notion-border rounded-lg shadow-sm">
                        <textarea
                            ref={textareaRef}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="w-full p-3 h-32 text-sm bg-white rounded-t-lg focus:outline-none resize-none placeholder-stone-400"
                            placeholder="Hi {{FirstName}}..."
                        ></textarea>

                        <div className="bg-notion-sidebar border-t border-notion-border rounded-b-lg px-2 py-1.5 flex items-center gap-2 overflow-x-auto no-scrollbar">
                            <span className="text-[10px] text-notion-text-light font-medium mr-1 flex-shrink-0">Insert:</span>
                            {BROADCAST_VARIABLES.map((variable) => (
                                <button
                                    key={variable.key}
                                    onClick={() => handleInsertVariable(variable, textareaRef, message, setMessage)}
                                    className="px-2 py-0.5 bg-white border border-notion-border rounded text-[10px] text-stone-600 hover:bg-stone-50 transition flex-shrink-0"
                                >
                                    {variable.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Validation Errors */}
                {validationErrors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        {validationErrors.map((error, index) => (
                            <div key={index} className="flex items-center gap-2 text-xs text-red-600">
                                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                                {error}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="border-t border-notion-border bg-stone-50/50 p-4">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-stone-500">
                        {getFilteredLeadCount() > 0
                            ? `${getFilteredLeadCount()} recipient${getFilteredLeadCount() !== 1 ? 's' : ''}`
                            : 'No recipients'}
                    </span>
                </div>
                <div className="flex gap-2">
                    <button onClick={onClose} className="flex-1 px-4 py-2 text-xs text-stone-500 hover:text-stone-800 transition border border-notion-border rounded">
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            const filteredLeads = getFilteredLeads();
                            let validation = { valid: true };
                            if (message && message.trim()) {
                                validation = validateBroadcast(filteredLeads, message, channel);
                            } else if (!attachments || attachments.length === 0) {
                                validation = validateBroadcast(filteredLeads, message, channel);
                            }
                            if (validation.valid) {
                                setValidationErrors([]);
                                setShowPreview(true);
                            } else {
                                setValidationErrors(validation.errors);
                            }
                        }}
                        disabled={getFilteredLeadCount() === 0 || (!message.trim() && attachments.length === 0)}
                        className="flex-1 px-4 py-2 bg-stone-800 hover:bg-stone-700 disabled:bg-stone-400 disabled:cursor-not-allowed text-white text-xs font-medium rounded shadow-sm transition"
                    >
                        Preview
                    </button>
                </div>
            </div>

            {/* Preview/Send Modal */}
            {showPreview && (
                <MobilePreviewModal
                    leads={getFilteredLeads()}
                    message={message}
                    subject={subject}
                    channel={channel}
                    sending={sending}
                    sendProgress={sendProgress}
                    userProfile={userProfile}
                    onBack={() => setShowPreview(false)}
                    onSend={async () => {
                        const filteredLeads = getFilteredLeads();
                        setSending(true);
                        setSendProgress({ processed: 0, total: filteredLeads.length });

                        try {
                            const userId = auth.currentUser?.uid;
                            if (!userId) throw new Error('Not authenticated');

                            const userData = {
                                displayName: userProfile?.displayName || userProfile?.fullName || '',
                                fullName: userProfile?.fullName || userProfile?.displayName || '',
                                email: auth.currentUser?.email || '',
                                phone: userProfile?.phone || '',
                            };

                            const result = await sendBroadcast(
                                userId,
                                filteredLeads,
                                message,
                                channel,
                                userData,
                                { subject: (channel === 'EMAIL' && subject) ? subject : undefined, attachments: attachments.length ? attachments : undefined },
                                (progress) => setSendProgress(progress)
                            );

                            setSendResult(result);

                            // CRITICAL: Failed messages were NOT saved to Firebase - only successful deliveries are persisted
                            if (onToastShow) {
                                if (result.failed === 0) {
                                    onToastShow({
                                        type: 'success',
                                        text: `✅ Broadcast delivered to ${result.successful} recipients`,
                                    });
                                } else if (result.successful > 0) {
                                    onToastShow({
                                        type: 'warning',
                                        text: `⚠️ Delivered to ${result.successful}, ${result.failed} failed (not saved)`,
                                    });
                                } else {
                                    onToastShow({
                                        type: 'error',
                                        text: `❌ Broadcast failed - no messages delivered`,
                                    });
                                }
                            }

                            setTimeout(() => onClose(), 1500);
                        } catch (error) {
                            console.error('Broadcast error:', error);
                            setValidationErrors([error.message]);
                            if (onToastShow) {
                                onToastShow({
                                    type: 'error',
                                    text: `❌ Failed: ${error.message}`,
                                });
                            }
                        } finally {
                            setSending(false);
                        }
                    }}
                />
            )}

            {/* Send Result */}
            {sendResult && (
                <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-stone-900/50">
                    <div className="bg-white w-full max-w-sm rounded-xl p-6 text-center">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                        <h2 className="font-semibold text-lg text-notion-text mb-2">Sent!</h2>
                        <p className="text-sm text-notion-text-light">
                            {sendResult.successful} recipient{sendResult.successful !== 1 ? 's' : ''}
                            {sendResult.failed > 0 && (
                                <span className="block text-amber-600 mt-1">
                                    {sendResult.failed} failed
                                </span>
                            )}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

// Mobile Preview Modal Component
const MobilePreviewModal = ({ leads, message, subject, channel, sending, sendProgress, userProfile, onBack, onSend }) => (
    <div className="fixed inset-0 z-60 flex flex-col bg-white">
        <div className="flex items-center justify-between p-4 border-b border-notion-border">
            <button onClick={onBack} className="text-stone-600 hover:text-stone-800">
                <ChevronLeft className="w-6 h-6" />
            </button>
            <h2 className="font-semibold text-sm text-notion-text">Preview</h2>
            <div className="w-6" />
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="bg-notion-sidebar rounded-lg p-4 border border-notion-border">
                <h3 className="text-xs font-semibold text-notion-text-light uppercase mb-2">Message via {channel}</h3>
                {channel === 'EMAIL' && subject && (
                    <p className="text-sm font-bold text-notion-text mb-2">
                        {leads.length > 0 ? parseBroadcastTemplate(subject, leads[0], userProfile || {}) : subject}
                    </p>
                )}
                <p className="text-sm text-notion-text whitespace-pre-wrap">
                    {leads.length > 0 ? parseBroadcastTemplate(message, leads[0], userProfile || {}) : message}
                </p>
            </div>

            <div>
                <h3 className="text-xs font-semibold text-notion-text-light uppercase mb-2">
                    Recipients ({leads.length})
                </h3>
                <div className="bg-white border border-notion-border rounded-lg overflow-hidden">
                    {leads.slice(0, 10).map((lead) => (
                        <div key={lead.id} className="px-4 py-3 border-b border-notion-border last:border-b-0">
                            <span className="text-sm text-notion-text block truncate">
                                {lead.fullName || lead.name || 'Unnamed'}
                            </span>
                            <span className="text-xs text-stone-400 block truncate">
                                {channel === 'SMS' ? lead.phone : lead.email}
                            </span>
                        </div>
                    ))}
                    {leads.length > 10 && (
                        <div className="px-4 py-3 text-sm text-stone-400">
                            + {leads.length - 10} more...
                        </div>
                    )}
                </div>
            </div>
        </div>

        <div className="border-t border-notion-border p-4">
            <button
                onClick={onSend}
                disabled={sending || leads.length === 0}
                className="w-full px-4 py-3 bg-stone-800 hover:bg-stone-700 disabled:bg-stone-400 text-white text-sm font-medium rounded shadow-sm flex items-center justify-center gap-2 transition"
            >
                {sending ? (
                    <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending {sendProgress.processed}/{sendProgress.total}...
                    </>
                ) : (
                    <>Send to {leads.length} recipient{leads.length !== 1 ? 's' : ''}</>
                )}
            </button>
        </div>
    </div>
);

export default MobileBroadcastModal;